/**
 * CustomerPickerCard — selectable customer list for rich AI responses.
 * Shows customer initials, name, meta info, optional tags (VIP, Member), radio-style selection.
 */
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export interface CustomerOption {
  id: string;
  name: string;
  initials: string;
  meta: string;
  tag?: string;
}

interface CustomerPickerCardProps {
  customers: CustomerOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  label?: string;
  className?: string;
}

export const CustomerPickerCard = ({
  customers,
  selectedId,
  onSelect,
  label = "I found matches — pick one",
  className,
}: CustomerPickerCardProps) => {
  return (
    <div className={cn("mt-2 space-y-1.5", className)}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray3">
        {label}
      </div>
      {customers.map((c) => {
        const on = selectedId === c.id;
        return (
          <div
            key={c.id}
            onClick={() => onSelect(c.id)}
            className={cn(
              "p-2.5 rounded-lg cursor-pointer flex items-center gap-2.5 transition-all duration-150 border-[1.5px]",
              on ? "bg-green4/50 border-greentab" : "bg-white border-gray6 hover:border-gray5"
            )}
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold bg-gray7 text-gray2">
              {c.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-black1">
                {c.name}
              </div>
              <div className="text-[11px] text-gray3">{c.meta}</div>
            </div>
            {c.tag && (
              <Badge className="bg-green4 text-green2 border-green4 text-[10px]">
                {c.tag}
              </Badge>
            )}
            <div
              className={cn(
                "w-4 h-4 rounded-full flex items-center justify-center border-2",
                on ? "border-green2 bg-green2" : "border-gray5 bg-white"
              )}
            >
              {on && (
                <span className="text-white text-[9px]">✓</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
